import React, { useEffect, useCallback, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useLocation } from "react-router-dom";
import { CircularProgress, Pagination } from "@ann/web-components";
import Icon from "../../../../../elements/icon/Icon";
import { MainWrapper, MainContainer, HeaderFiltersContainer, WorkspacesContainer, WorkspacesGridWrapper, LoadingContainer, PaginationContainer, CustomGenericListStyle, NoDataWrapper, NoDataTitle, NoDataDescription } from "./Main.styled";
import { Header, Filters, WorkspaceCard, CreateWorkspaceModal } from "./components";
import { useStore } from "../../../../../hooks";

const Main = ({ breadcrumbHelpers }) => {
  const { workspaceStore } = useStore();
  const location = useLocation();
  const searchTimeoutRef = useRef(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // El fetch inicial de la lista de workspaces se hace ahora en App.jsx

  // Resetear breadcrumb cuando estamos en la ruta principal de workspace
  useEffect(() => {
    // Verificar que estamos en la ruta base de workspace (sin workspaceId)
    const isMainRoute = location.pathname === '/v2/workspace' || location.pathname === '/v2/workspace/';
    if (isMainRoute) {
      breadcrumbHelpers?.clear?.();
    }
  }, [location.pathname, breadcrumbHelpers]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handlePageChange = useCallback((page) => {
    workspaceStore.setCurrentPage(page);
    workspaceStore.fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleItemsPerPageChange = useCallback((itemsPerPage) => {
    workspaceStore.setItemsPerPage(itemsPerPage);
    workspaceStore.fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = useCallback((value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      const searchParams = value ? { keyword: value } : {};
      workspaceStore.setSearchParams(searchParams);
      workspaceStore.fetchPage();
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSortChange = useCallback((sortOption) => {
    // When changing sort field, maintain current order_direction or default to 'desc'
    const currentDirection = workspaceStore.sortParams.order_direction?.[0] || 'desc';
    workspaceStore.setSortParams({
      order_by: [sortOption],
      order_direction: [currentDirection],
    });
    workspaceStore.fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOrderChange = useCallback((currentOrder) => {
    const newOrder = currentOrder === 'asc' ? 'desc' : 'asc';
    workspaceStore.setSortParams({
      order_by: workspaceStore.sortParams.order_by,
      order_direction: [newOrder],
    });
    workspaceStore.fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
    <CustomGenericListStyle />
    <MainWrapper>
      <HeaderFiltersContainer>
        <Header onCreateClick={() => setIsCreateModalOpen(true)} />
        <Filters 
          onSearchChange={handleSearchChange}
          onSortChange={handleSortChange}
          onOrderChange={handleOrderChange}
          selectedSortOption={workspaceStore.sortParams.order_by?.[0] || "created_at"}
          orderDirection={workspaceStore.sortParams.order_direction?.[0]?.toLowerCase() || "desc"}
          loading={workspaceStore.isLoading}
        />
      </HeaderFiltersContainer>
      <MainContainer>
        <WorkspacesContainer>
          {workspaceStore.isLoading && workspaceStore.workspaceList.length === 0 ? (
            <LoadingContainer>
              <CircularProgress size="xl" />
            </LoadingContainer>
          ) : workspaceStore.workspaceList.length === 0 ? (
            <NoDataWrapper>
              <Icon name="noDataAvailable" width="201px" height="195px" />
              <NoDataTitle>No workspaces yet</NoDataTitle>
              <NoDataDescription>
                No workspaces found. Create your first workspace to get started.
              </NoDataDescription>
            </NoDataWrapper>
          ) : (
            <>
              {workspaceStore.isLoading && workspaceStore.workspaceList.length > 0 && (
                <LoadingContainer>
                  <CircularProgress size="xl" />
                </LoadingContainer>
              )}
              
              {!workspaceStore.isLoading && (
                <WorkspacesGridWrapper>
                  {workspaceStore.workspaceList.map((workspace) => (
                    <WorkspaceCard
                      key={workspace.workspaceUuid || workspace.tenant_uuid || workspace.tenant_id || workspace.id}
                      workspace={workspace}
                    />
                  ))}
                </WorkspacesGridWrapper>
              )}
            </>
          )}
        </WorkspacesContainer>
        {workspaceStore.workspaceList.length > 0 && (
          <PaginationContainer>
            <Pagination
              currentPage={workspaceStore.currentPage}
              itemsPerPage={workspaceStore.itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              onPageChange={handlePageChange}
              totalItems={workspaceStore.meta?.total_items || 0}
              totalPages={workspaceStore.meta?.total_pages || 1}
            />
          </PaginationContainer>
        )}
      </MainContainer>
    </MainWrapper>
    
    <CreateWorkspaceModal 
      isOpen={isCreateModalOpen}
      onClose={() => setIsCreateModalOpen(false)}
    />
    </>
  );
};  

export default observer(Main);

